import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Car,
  Wrench,
  Receipt,
  Package,
  Users,
  TrendingUp,
  AlertTriangle,
  CalendarClock,
  ArrowUpRight,
  Sparkles,
  ShieldCheck,
  CheckCircle2,
  Clock,
} from "lucide-react";
import api, { fmtINR, fmtDateTime, fmtDate } from "../lib/api";
import { Page, StatusPill } from "../components/Primitives";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";

// ---------- Tile variants ----------

// Hero tile: large, prominent, colored accent
const HeroTile = ({ label, value, context, icon: Icon, onClick, accent, test }) => (
  <motion.button
    whileHover={{ y: -3 }}
    onClick={onClick}
    data-testid={test}
    className="group relative text-left p-6 md:p-7 bg-[var(--kk-surface)] border border-[var(--kk-border)] overflow-hidden transition-all hover:border-[var(--kk-black)] hover:shadow-[0_16px_40px_rgba(0,0,0,0.08)]"
  >
    <div
      className="absolute inset-x-0 top-0 h-1"
      style={{ background: accent }}
    />
    <div className="flex items-start justify-between">
      <span className="kk-overline">{label}</span>
      <div
        className="w-8 h-8 flex items-center justify-center border border-[var(--kk-border)] group-hover:bg-[var(--kk-black)] group-hover:text-white transition"
      >
        <Icon size={14} />
      </div>
    </div>
    <div className="mt-5 font-display text-4xl md:text-5xl leading-none tracking-tight">
      {value}
    </div>
    <div className="mt-3 text-xs text-[var(--kk-muted)] flex items-center gap-1.5 uppercase tracking-wider">
      {context}
      <ArrowUpRight size={11} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
    </div>
  </motion.button>
);

// Compact tile: smaller, grouped under section headers
const CompactTile = ({ label, value, icon: Icon, onClick, test, tone = "default" }) => {
  const toneCls =
    tone === "warn"
      ? "border-l-[var(--kk-warning)]"
      : tone === "danger"
      ? "border-l-[var(--kk-danger)]"
      : tone === "lime"
      ? "border-l-[var(--kk-lime)]"
      : "border-l-[var(--kk-border)]";
  return (
    <motion.button
      whileHover={{ y: -2 }}
      onClick={onClick}
      data-testid={test}
      className={`group text-left p-5 bg-[var(--kk-surface)] border border-[var(--kk-border)] border-l-[3px] ${toneCls} transition-all hover:border-[var(--kk-black)] hover:border-l-[3px] hover:shadow-[0_10px_24px_rgba(0,0,0,0.06)]`}
    >
      <div className="flex items-center justify-between">
        <Icon size={14} className="text-[var(--kk-muted)] group-hover:text-[var(--kk-black)] transition" />
        <ArrowUpRight size={12} className="text-[var(--kk-muted)] opacity-0 group-hover:opacity-100 transition" />
      </div>
      <div className="mt-3 font-display text-3xl leading-none">{value}</div>
      <div className="mt-2 text-[0.65rem] uppercase tracking-[0.14em] text-[var(--kk-muted)]">{label}</div>
    </motion.button>
  );
};

// Section header
const Section = ({ num, title, right, children }) => (
  <section className="mt-10 first:mt-8">
    <div className="flex items-end justify-between mb-4 pb-3 border-b border-[var(--kk-border)]">
      <div className="flex items-baseline gap-3">
        <span className="kk-overline">{num}</span>
        <h3 className="font-display text-base md:text-lg uppercase leading-none tracking-tight">{title}</h3>
      </div>
      {right}
    </div>
    {children}
  </section>
);

export default function Dashboard() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Permission helpers — driven by effective_perms returned by /api/auth/me
  const isAdmin = user?.role === "admin" || user?.user_type === "admin";
  const can = (mod) => isAdmin || !!user?.permissions?.[mod]?.view;
  const canSeeRevenue = isAdmin || user?.role === "accountant" || can("accounting");

  const load = async () => {
    try {
      const { data } = await api.get("/dashboard/summary");
      setData(data);
    } finally {
      setLoading(false);
    }
  };

  const seed = async () => {
    try {
      await api.post("/seed/demo");
      toast.success("Demo data loaded.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Seed failed");
    }
  };

  useEffect(() => {
    load();
  }, []);

  if (loading || !data) {
    return <Page number="00" title="Dashboard" subtitle="Loading the floor…" />;
  }

  const k = data.kpis;
  const today = new Date().toLocaleDateString("en-IN", { weekday: "long", day: "2-digit", month: "long" });

  // Figure out how many hero tiles render so we can size the grid
  const heroes = [
    canSeeRevenue && "revenue",
    can("service") && "jobcards",
    (can("leads") || can("showroom")) && "leads",
  ].filter(Boolean);
  const heroCols = heroes.length === 1 ? "md:grid-cols-1" : heroes.length === 2 ? "md:grid-cols-2" : "md:grid-cols-3";

  return (
    <Page
      number="01"
      title="Today at Karr Kraft"
      subtitle={today + " · everything clicks through to the source."}
      right={
        isAdmin ? (
          <button className="kk-btn-ghost" onClick={seed} data-testid="seed-demo">
            <Sparkles size={14} /> Load Demo Data
          </button>
        ) : null
      }
    >
      {/* HEADLINE ROW */}
      {heroes.length > 0 && (
        <div className={"grid grid-cols-1 gap-4 md:gap-5 " + heroCols}>
          {canSeeRevenue && (
            <HeroTile
              label="Month Revenue"
              value={fmtINR(k.month_revenue)}
              context={`${k.today_deliveries} deliveries today`}
              icon={TrendingUp}
              accent="var(--kk-lime)"
              onClick={() => nav("/reports")}
              test="hero-revenue"
            />
          )}
          {can("service") && (
            <HeroTile
              label="Active Job Cards"
              value={k.active_job_cards}
              context={can("accounting") ? `${k.pending_invoices} invoices pending` : `${k.today_deliveries} deliveries today`}
              icon={Wrench}
              accent="var(--kk-black)"
              onClick={() => nav("/service/jobcards")}
              test="hero-jobcards"
            />
          )}
          {(can("leads") || can("showroom")) && (
            <HeroTile
              label="Leads Open"
              value={k.leads_open}
              context={can("showroom") ? `${k.cars_available + k.cars_reserved} vehicles in stock` : "Across pipeline"}
              icon={Users}
              accent="#f59e0b"
              onClick={() => nav(can("leads") ? "/leads" : "/showroom")}
              test="hero-leads"
            />
          )}
        </div>
      )}

      {/* Empty state — user has no section-level access at all */}
      {heroes.length === 0 && !can("showroom") && !can("service") && !can("warranty") && (
        <div className="kk-card p-10 text-center border-dashed">
          <Sparkles className="mx-auto mb-3 text-[var(--kk-muted)]" size={22} />
          <div className="font-display text-xl uppercase">Welcome back, {user?.name?.split(" ")[0] || "there"}</div>
          <p className="text-sm text-[var(--kk-muted)] mt-2 max-w-md mx-auto">
            Your dashboard is intentionally quiet — ask an admin to grant module access if you need to see revenue, job cards or the showroom.
          </p>
        </div>
      )}

      {/* SHOWROOM */}
      {can("showroom") && (
      <Section
        num="01"
        title="Showroom"
        right={
          <button onClick={() => nav("/showroom")} className="text-[0.7rem] uppercase tracking-wider underline">
            View all
          </button>
        }
      >
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          <CompactTile label="Cars Available" value={k.cars_available} icon={Car} tone="lime"
            onClick={() => nav("/showroom?status=available")} test="kpi-available" />
          <CompactTile label="Cars Reserved" value={k.cars_reserved} icon={Car}
            onClick={() => nav("/showroom?status=reserved")} test="kpi-reserved" />
          <CompactTile label="Upcoming Test Drives" value={data.upcoming_test_drives.length} icon={CalendarClock}
            onClick={() => nav("/service/testdrives")} test="kpi-testdrives" />
          {can("leads") && (
            <CompactTile label="Leads in Pipeline" value={k.leads_open} icon={Users}
              onClick={() => nav("/leads")} test="kpi-leads" />
          )}
        </div>
      </Section>
      )}

      {/* SERVICE SHOP */}
      {can("service") && (
      <Section
        num={can("showroom") ? "02" : "01"}
        title="Service Shop"
        right={
          <button onClick={() => nav("/service/jobcards")} className="text-[0.7rem] uppercase tracking-wider underline">
            View all
          </button>
        }
      >
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          <CompactTile label="Active Job Cards" value={k.active_job_cards} icon={Wrench} tone="lime"
            onClick={() => nav("/service/jobcards")} test="kpi-jobcards" />
          <CompactTile label="Deliveries Today" value={k.today_deliveries} icon={CheckCircle2}
            onClick={() => nav("/service/jobcards?status=delivered")} test="kpi-deliveries" />
          {can("stores") && (
            <CompactTile label="Low Stock Alerts" value={k.low_stock} icon={AlertTriangle}
              tone={k.low_stock > 0 ? "warn" : "default"}
              onClick={() => nav("/stores?low=1")} test="kpi-lowstock" />
          )}
          {can("accounting") && (
            <CompactTile label="Pending Invoices" value={k.pending_invoices} icon={Receipt}
              onClick={() => nav("/accounting?status=sent")} test="kpi-invoices" />
          )}
        </div>
      </Section>
      )}

      {/* WARRANTY */}
      {can("warranty") && (
      <Section
        num={[can("showroom"), can("service")].filter(Boolean).length + 1 + ""}
        title="Warranty"
        right={
          <button onClick={() => nav("/warranty")} className="text-[0.7rem] uppercase tracking-wider underline">
            View all
          </button>
        }
      >
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          <CompactTile label="Active Warranties" value={k.warranties_active || 0} icon={ShieldCheck} tone="lime"
            onClick={() => nav("/warranty?status=active")} test="kpi-war-active" />
          <CompactTile label="Expiring ≤ 30d" value={k.warranties_expiring || 0} icon={Clock}
            tone={(k.warranties_expiring || 0) > 0 ? "warn" : "default"}
            onClick={() => nav("/warranty?status=expiring")} test="kpi-war-expiring" />
          {can("stores") && (
            <CompactTile label="Stores Inventory" value={k.low_stock > 0 ? `${k.low_stock} low` : "OK"} icon={Package}
              onClick={() => nav("/stores")} test="kpi-inventory" />
          )}
          {can("leads") && (
            <CompactTile label="Open Leads" value={k.leads_open} icon={Users}
              onClick={() => nav("/leads")} test="kpi-leads-2" />
          )}
        </div>
      </Section>
      )}

      {/* Activity streams */}
      {(can("service") || can("leads") || can("stores")) && (
      <section className="grid lg:grid-cols-3 gap-5 mt-10">
        {can("service") && (
        <div className="kk-card p-5">
          <div className="flex items-center justify-between mb-4 pb-3 border-b border-[var(--kk-border)]">
            <div>
              <div className="kk-overline">Recent</div>
              <div className="font-display text-base uppercase mt-0.5">Job Cards</div>
            </div>
            <button className="text-xs uppercase tracking-wider underline" onClick={() => nav("/service/jobcards")} data-testid="more-jobcards">
              All
            </button>
          </div>
          {data.recent_job_cards.length === 0 && (
            <div className="text-sm text-[var(--kk-muted)] py-4 text-center border border-dashed border-[var(--kk-border)]">
              No recent job cards.
            </div>
          )}
          <ul className="space-y-3">
            {data.recent_job_cards.map((j) => (
              <li
                key={j.id}
                className="flex items-start justify-between gap-3 border-b border-[var(--kk-border)] pb-3 last:border-0 last:pb-0 cursor-pointer hover:translate-x-0.5 transition"
                onClick={() => nav(`/service/jobcards/${j.id}`)}
              >
                <div className="min-w-0 flex-1">
                  <div className="font-mono text-[0.65rem] text-[var(--kk-muted)]">{j.code}</div>
                  <div className="font-semibold text-sm truncate">{j.customer_name}</div>
                  <div className="text-xs text-[var(--kk-muted)] truncate">
                    {j.make} {j.model} · {j.registration_no}
                  </div>
                </div>
                <StatusPill status={j.status} />
              </li>
            ))}
          </ul>
        </div>
        )}

        {can("leads") && (
        <div className="kk-card p-5">
          <div className="flex items-center justify-between mb-4 pb-3 border-b border-[var(--kk-border)]">
            <div>
              <div className="kk-overline">Pipeline</div>
              <div className="font-display text-base uppercase mt-0.5">Leads</div>
            </div>
            <button className="text-xs uppercase tracking-wider underline" onClick={() => nav("/leads")}>
              All
            </button>
          </div>
          {data.recent_leads.length === 0 && (
            <div className="text-sm text-[var(--kk-muted)] py-4 text-center border border-dashed border-[var(--kk-border)]">
              No leads yet.
            </div>
          )}
          <ul className="space-y-3">
            {data.recent_leads.map((l) => (
              <li key={l.id} className="flex items-start justify-between gap-3 border-b border-[var(--kk-border)] pb-3 last:border-0 last:pb-0">
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-sm truncate">{l.name}</div>
                  <div className="text-xs text-[var(--kk-muted)]">{l.phone} · {l.source}</div>
                </div>
                <StatusPill status={l.stage} />
              </li>
            ))}
          </ul>
        </div>
        )}

        {can("stores") && (
        <div className="kk-card p-5">
          <div className="flex items-center justify-between mb-4 pb-3 border-b border-[var(--kk-border)]">
            <div>
              <div className="kk-overline">Attention</div>
              <div className="font-display text-base uppercase mt-0.5">Low Stock</div>
            </div>
            <button className="text-xs uppercase tracking-wider underline" onClick={() => nav("/stores?low=1")}>
              All
            </button>
          </div>
          {data.low_stock_items.length === 0 && (
            <div className="text-sm text-[var(--kk-muted)] py-4 text-center border border-dashed border-[var(--kk-border)]">
              All stocked up.
            </div>
          )}
          <ul className="space-y-3">
            {data.low_stock_items.map((i) => (
              <li key={i.id} className="flex items-center justify-between border-b border-[var(--kk-border)] pb-3 last:border-0 last:pb-0">
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-sm truncate">{i.name}</div>
                  <div className="text-[0.65rem] font-mono text-[var(--kk-muted)]">{i.sku}</div>
                </div>
                <div className="text-right flex-shrink-0 ml-3">
                  <div className="font-display text-lg leading-none">{i.qty}</div>
                  <div className="text-[0.55rem] uppercase tracking-wider text-[var(--kk-muted)] mt-0.5">
                    min {i.min_qty}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
        )}
      </section>
      )}

      {/* Expiring warranties strip */}
      {can("warranty") && (data.expiring_warranties || []).length > 0 && (
        <section className="kk-card p-5 mt-5" data-testid="expiring-warranties">
          <div className="flex items-center justify-between mb-4 pb-3 border-b border-[var(--kk-border)]">
            <div className="flex items-center gap-2">
              <ShieldCheck size={14} className="text-[var(--kk-warning)]" />
              <div className="font-display text-base uppercase">Warranties Expiring Soon</div>
            </div>
            <button className="text-xs uppercase tracking-wider underline" onClick={() => nav("/warranty?status=expiring")}>
              All
            </button>
          </div>
          <ul className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
            {data.expiring_warranties.map((w) => {
              const daysLeft = Math.floor((new Date(w.end_date) - new Date()) / (1000 * 60 * 60 * 24));
              const urgent = daysLeft <= 7;
              return (
                <li
                  key={w.id}
                  className="flex items-center justify-between p-3 border border-[var(--kk-border)] cursor-pointer hover:border-[var(--kk-black)] transition"
                  onClick={() => nav("/warranty?status=expiring")}
                >
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-sm truncate">{w.title}</div>
                    <div className="text-xs text-[var(--kk-muted)] truncate">
                      {w.customer_name} · {w.vehicle_reg}
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0 ml-3">
                    <div className={"font-display text-lg leading-none " + (urgent ? "text-[var(--kk-danger)]" : "")}>
                      {daysLeft}d
                    </div>
                    <div className="text-[0.55rem] uppercase tracking-wider text-[var(--kk-muted)] mt-0.5">
                      {fmtDate(w.end_date)}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      {can("showroom") && data.upcoming_test_drives.length > 0 && (
        <section className="kk-card p-5 mt-5">
          <div className="flex items-center justify-between mb-4 pb-3 border-b border-[var(--kk-border)]">
            <div className="flex items-center gap-2">
              <CalendarClock size={14} />
              <div className="font-display text-base uppercase">Upcoming Test Drives</div>
            </div>
            <button className="text-xs uppercase tracking-wider underline" onClick={() => nav("/service/testdrives")}>
              All
            </button>
          </div>
          <ul className="divide-y divide-[var(--kk-border)]">
            {data.upcoming_test_drives.map((t) => (
              <li key={t.id} className="py-3 flex items-center justify-between">
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-sm truncate">{t.customer_name}</div>
                  <div className="text-xs text-[var(--kk-muted)]">{t.phone}</div>
                </div>
                <div className="text-sm font-mono text-[var(--kk-muted)] flex-shrink-0 ml-3">
                  {fmtDateTime(t.scheduled_at)}
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}
    </Page>
  );
}
